import type { FilterGroup, FilterCondition, DefaultFilter } from '../../types';
import { generateId } from '../../utils';

export function createEmptyFilterGroup(): FilterGroup {
  return { type: 'group', id: generateId(), logic: 'AND', negated: false, children: [] };
}

export function createFilterCondition(column: string, operator: string, value: string, value2?: string): FilterCondition {
  return { type: 'condition', id: generateId(), column, operator, value, value2 };
}

/** Deep-clone a filter group, replacing all IDs to avoid duplication. */
export function cloneFilterGroup(group: FilterGroup): FilterGroup {
  return {
    ...group,
    id: generateId(),
    children: group.children.map((child) =>
      child.type === 'group'
        ? cloneFilterGroup(child)
        : { ...child, id: generateId() }
    ),
  };
}

/** Remove a node by ID from a filter group tree. Returns a new tree (immutable). */
export function removeFromGroup(group: FilterGroup, targetId: string): FilterGroup {
  return {
    ...group,
    children: group.children
      .filter((child) => child.id !== targetId)
      .map((child) =>
        child.type === 'group' ? removeFromGroup(child, targetId) : child
      ),
  };
}

/** Update a node by ID in a filter group tree. Returns a new tree (immutable). */
export function updateInGroup(
  group: FilterGroup,
  targetId: string,
  updater: (node: FilterCondition | FilterGroup) => FilterCondition | FilterGroup
): FilterGroup {
  return {
    ...group,
    children: group.children.map((child) => {
      if (child.id === targetId) return updater(child);
      if (child.type === 'group') return updateInGroup(child, targetId, updater);
      return child;
    }),
  };
}

/** Add a child to a specific group by ID. */
export function addToGroup(
  group: FilterGroup,
  parentId: string,
  child: FilterCondition | FilterGroup
): FilterGroup {
  if (group.id === parentId) {
    return { ...group, children: [...group.children, child] };
  }
  return {
    ...group,
    children: group.children.map((c) =>
      c.type === 'group' ? addToGroup(c, parentId, child) : c
    ),
  };
}

/** Serialize FilterGroup to the flat array format the backend API expects. */
export function serializeFilterGroup(group: FilterGroup): any {
  const children: any[] = [];
  for (const child of group.children) {
    if (child.type === 'group') {
      const serialized = serializeFilterGroup(child);
      // Only include groups that have children
      if (serialized.children.length > 0) {
        children.push(serialized);
      }
      continue;
    }
    // Skip incomplete conditions (no dimension selected or no value for non-null operators)
    if (!child.column) continue;
    const needsNoValue = child.operator === 'IS NULL' || child.operator === 'IS NOT NULL';
    if (!needsNoValue && !child.value) continue;
    if (child.operator === 'BETWEEN' && !child.value2) continue;

    const cond: any = { type: 'condition', column: child.column, operator: child.operator };
    if (child.operator === 'BETWEEN' && child.value2) {
      cond.value = [child.value, child.value2];
    } else if ((child.operator === 'IN' || child.operator === 'NOT IN') && typeof child.value === 'string') {
      const parts = child.value.split(',').map((v: string) => v.trim()).filter(Boolean);
      cond.value = parts.length > 1 ? parts : child.value;
    } else {
      cond.value = child.value;
    }
    children.push(cond);
  }
  return {
    type: 'group',
    logic: group.logic,
    negated: group.negated,
    children,
  };
}

/** Create filter conditions from model default_filters, returns conditions and their IDs. */
export function createDefaultFilterConditions(
  defaultFilters: DefaultFilter[]
): { conditions: FilterCondition[]; ids: Set<string> } {
  const conditions: FilterCondition[] = [];
  const ids = new Set<string>();
  for (const df of defaultFilters) {
    const cond = createFilterCondition(
      df.dimension,
      df.operator,
      Array.isArray(df.value) ? df.value.join(', ') : String(df.value)
    );
    conditions.push(cond);
    ids.add(cond.id);
  }
  return { conditions, ids };
}

/** Strip default filter conditions from a filter group (for serialization). */
export function stripDefaultFilters(group: FilterGroup, lockedIds: Set<string>): FilterGroup {
  return {
    ...group,
    children: group.children
      .filter((child) => !(child.type === 'condition' && lockedIds.has(child.id)))
      .map((child) =>
        child.type === 'group' ? stripDefaultFilters(child, lockedIds) : child
      ),
  };
}

/** Recursively remove filter conditions matching given column names, preserving locked defaults. */
export function removeFiltersByColumn(group: FilterGroup, columns: Set<string>, lockedIds: Set<string>): FilterGroup {
  return {
    ...group,
    children: group.children
      .filter((child) => {
        if (child.type === 'condition') {
          // Never remove locked default filters
          if (lockedIds.has(child.id)) return true;
          return !columns.has(child.column);
        }
        return true;
      })
      .map((child) =>
        child.type === 'group' ? removeFiltersByColumn(child, columns, lockedIds) : child
      ),
  };
}

/** Check if a filter group has any actual conditions. */
export function hasConditions(group: FilterGroup): boolean {
  return group.children.some((child) =>
    child.type === 'condition' || (child.type === 'group' && hasConditions(child))
  );
}

/** Convert legacy flat QueryFilter[] to a FilterGroup. */
export function filtersToGroup(filters: { column: string; operator: string; value: string }[]): FilterGroup {
  const group = createEmptyFilterGroup();
  group.children = filters.map((f) => createFilterCondition(f.column, f.operator, f.value));
  return group;
}

/** Deserialize a stored filter group JSON back to internal FilterGroup with fresh IDs. */
export function deserializeFilterGroup(stored: any): FilterGroup {
  if (!stored || stored.type !== 'group') {
    return createEmptyFilterGroup();
  }
  return {
    type: 'group',
    id: generateId(),
    logic: stored.logic || 'AND',
    negated: !!stored.negated,
    children: (stored.children || []).map((child: any) => {
      if (child.type === 'group') {
        return deserializeFilterGroup(child);
      }
      // Condition: handle BETWEEN value array -> value/value2 split,
      // and IN/NOT IN array -> comma-separated string
      let value = child.value ?? '';
      let value2: string | undefined;
      if (child.operator === 'BETWEEN' && Array.isArray(child.value)) {
        value = String(child.value[0] ?? '');
        value2 = String(child.value[1] ?? '');
      } else if ((child.operator === 'IN' || child.operator === 'NOT IN') && Array.isArray(child.value)) {
        value = child.value.join(', ');
      } else {
        value = String(value);
      }
      const cond: FilterCondition = {
        type: 'condition',
        id: generateId(),
        column: child.column || child.field || child.dimension || '',
        operator: child.operator || '=',
        value,
        value2,
      };
      return cond;
    }),
  };
}
